import { useEffect, useId, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'

const SIZES = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' } as const

/**
 * A centred dialog: dimmed backdrop (click to close), Escape to close, a titled header, a scrolling body
 * and an optional footer for the actions. Replaces the hand-rolled overlay each screen used to repeat.
 */
export default function Modal({
  title,
  icon,
  onClose,
  children,
  footer,
  size = 'md',
  tone = 'default',
}: {
  title: ReactNode
  icon?: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  size?: keyof typeof SIZES
  /** "danger" tints the title for destructive confirmations. */
  tone?: 'default' | 'danger'
}) {
  const titleId = useId()
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn('relative bg-white rounded-xl shadow-2xl w-full max-h-[85vh] flex flex-col overflow-hidden', SIZES[size])}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <h2
            id={titleId}
            className={cn(
              'flex items-center gap-2 text-base font-semibold min-w-0',
              tone === 'danger' ? 'text-danger' : 'text-brand-darkest',
            )}
          >
            {icon}
            <span className="truncate">{title}</span>
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">{children}</div>
        {footer && (
          <div className="px-5 py-3.5 border-t border-gray-100 flex items-center justify-end gap-2">{footer}</div>
        )}
      </div>
    </div>
  )
}
